import { env } from '@/shared/config/env'

export type ImageFit = 'cover' | 'contain' | 'inside' | 'outside'
export type ImageFormat = 'webp' | 'avif' | 'jpeg' | 'png' | 'auto'
export type ImagePreset = 'thumb' | 'card' | 'hero' | 'full'

interface ImagePresetConfig {
  width: number
  height?: number
  quality: number
  fit: ImageFit
  widths: number[]
  sizes: string
}

export interface OptimizedImageOptions {
  preset?: ImagePreset
  width?: number
  height?: number
  quality?: number
  fit?: ImageFit
  format?: ImageFormat
}

const IMAGE_RESIZE_PATH = '/api/public/images/resize'
const PLACEHOLDER_SRC = '/placeholder-product.svg'
const OPTIMIZABLE_HOSTS = new Set([
  'drakonisoroka.ru',
  'www.drakonisoroka.ru',
  'app.drakonisoroka.ru',
  'api.drakonisoroka.ru',
  'localhost',
  '127.0.0.1',
])

export const IMAGE_PRESETS: Record<ImagePreset, ImagePresetConfig> = {
  thumb: {
    width: 128,
    height: 128,
    quality: 78,
    fit: 'cover',
    widths: [96, 128, 192],
    sizes: '128px',
  },
  card: {
    width: 384,
    height: 384,
    quality: 80,
    fit: 'cover',
    widths: [256, 384, 512],
    sizes: '(min-width: 640px) 220px, 50vw',
  },
  hero: {
    width: 1080,
    quality: 82,
    fit: 'inside',
    widths: [640, 750, 1080, 1200],
    sizes: '100vw',
  },
  full: {
    width: 1600,
    quality: 85,
    fit: 'inside',
    widths: [1080, 1600, 1920],
    sizes: '100vw',
  },
}

const preloadedImages = new Set<string>()

export function getOptimizedImageUrl(src: string | null | undefined, options: OptimizedImageOptions = {}): string {
  const normalizedSrc = normalizeImageSrc(src)
  if (!canOptimizeImage(normalizedSrc)) {
    return normalizedSrc
  }

  const preset = IMAGE_PRESETS[options.preset || 'card']
  const width = options.width || preset.width
  const height = options.height ?? preset.height
  const quality = options.quality || preset.quality
  const fit = options.fit || preset.fit
  const format = options.format || 'webp'
  const base = getImageResizeBaseUrl()
  const params = new URLSearchParams({
    src: normalizedSrc,
    w: String(width),
    q: String(quality),
    fit,
    format,
  })

  if (height) {
    params.set('h', String(height))
  }

  return `${base}?${params.toString()}`
}

export function getOptimizedImageSrcSet(src: string | null | undefined, options: OptimizedImageOptions = {}): string | undefined {
  const normalizedSrc = normalizeImageSrc(src)
  if (!canOptimizeImage(normalizedSrc)) {
    return undefined
  }

  const preset = IMAGE_PRESETS[options.preset || 'card']
  return preset.widths
    .map((width) => {
      const height = options.height ?? preset.height
      const scaledHeight = height ? Math.round((height / preset.width) * width) : undefined

      return `${getOptimizedImageUrl(normalizedSrc, {
        ...options,
        width,
        height: scaledHeight,
      })} ${width}w`
    })
    .join(', ')
}

export function getOptimizedImageSizes(preset: ImagePreset = 'card'): string {
  return IMAGE_PRESETS[preset].sizes
}

export function preloadOptimizedImage(src: string | null | undefined, options: OptimizedImageOptions = {}): void {
  if (typeof window === 'undefined') return

  const url = getOptimizedImageUrl(src, options)
  if (!url || preloadedImages.has(url)) return

  preloadedImages.add(url)
  const image = new Image()
  image.decoding = 'async'
  image.src = url
}

export function normalizeImageSrc(src: string | null | undefined): string {
  return src?.trim() || PLACEHOLDER_SRC
}

export function canOptimizeImage(src: string): boolean {
  if (!src || src === PLACEHOLDER_SRC) return false
  if (src.startsWith('data:') || src.startsWith('blob:')) return false
  if (src.includes(IMAGE_RESIZE_PATH)) return false
  if (/\.svg(?:[?#]|$)/i.test(src)) return false

  try {
    const url = new URL(src, getCurrentOrigin())
    return OPTIMIZABLE_HOSTS.has(url.hostname) && (
      url.pathname.startsWith('/storage/')
      || url.pathname.startsWith('/images/')
      || src.startsWith('/storage/')
      || src.startsWith('/images/')
    )
  } catch {
    return false
  }
}

function getImageResizeBaseUrl(): string {
  const explicit = import.meta.env.VITE_IMAGE_PROXY_URL as string | undefined
  if (explicit) return explicit

  const apiBase = env.apiUrl.replace(/\/$/, '')
  return `${apiBase}${IMAGE_RESIZE_PATH}`
}

function getCurrentOrigin(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }

  return 'https://app.drakonisoroka.ru'
}
